import { Injectable } from '@angular/core';
import { SxSnackbarService } from './sx-snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { filter, take, switchMap, catchError } from 'rxjs/operators';
import { Subscription, Observable } from 'rxjs';
import { environment } from '@environment';
import { SxDialogService } from './sx-dialog.service';
import { ErrorMessage } from '@model';

@Injectable({
  providedIn: 'root'
})
export class SxExportService {
  // INFORMATION: Part of tis component is not used due to IE problem with Excel, use SxClientExportService instead!
  // When Mina sidor doesen't have to support IE anymore, use till service instead of SxClientExportService.
  // Un-comment commented out parts, mainly used i Analyze table component
  constructor(
    private sxSnackbarService: SxSnackbarService, 
    private sxDialogService: SxDialogService,
    private translate: TranslateService) {}

  exportInFrame = (
    keyFetch: Observable<string>,
    exportDataFetch: (key: string) => Observable<any>,
    exportDoneCallback: () => void,
    fileName: string
  ): Subscription => {
    this.sxSnackbarService.openSnackBar(this.translate.instant('core.messages.export_begin'), '');

    return keyFetch
      .pipe(
        filter(key => !!key && key !== ''),
        take(1),
        switchMap((key: string) => {
          console.log('key: ' + key);
          return exportDataFetch(key);
        })
      )
      .subscribe((blob: any) => {
        // For other browsers:
        // Create a link pointing to the ObjectURL containing the blob.
        const data = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = data;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          // For Firefox it is necessary to delay revoking the ObjectURL
          window.URL.revokeObjectURL(data);
        }, 100);
        this.sxSnackbarService.openSnackBar(this.translate.instant('core.messages.export_done'), '');
        exportDoneCallback();
      });
  };

  exportInNewWindow = (
    preDownloadStringKey: string,
    keyFetch: Observable<string>,
    urlCallback: string
  ): Subscription => {
    let ts = '' + new Date().getTime();
 
    this.sxSnackbarService.openSnackBar(this.translate.instant(preDownloadStringKey), '');
 
    let win = window.open('about:blank', ts);
    win.location.replace('/report-download.html?ts=' + ts);
    win['selfRef'] = win;
 
    return keyFetch
      .pipe(
        filter(x => !!x),
        take(1)
      )
      .subscribe(
        key => {
          let reportUrl = `${environment.apiEndpoint}/Reports/${key}` + urlCallback;
 
          win.location.replace('/report-download.html?ts=' + ts + '&redir=' + encodeURIComponent(reportUrl));
          win['selfRef'] = win;
          win.focus();
        },
        _ => {
          let errorUrl = `${window.location.origin}/error`;
          win.location.href = errorUrl;
          win.focus();
        }
      );
  };

  exportInNewWindowCloseOnError = (
    preDownloadStringKey: string,
    keyFetch: Observable<string>,
    errorMessage: string,
    errorButton: string,
    errorHeader: string,
    urlCallback: string
  ): Subscription => {
    let ts = '' + new Date().getTime();

    this.sxSnackbarService.openSnackBar(this.translate.instant(preDownloadStringKey), '');

    let win = window.open('about:blank', ts);
    win.location.replace('/report-download.html?ts=' + ts);
    win['selfRef'] = win;

    return keyFetch
      .pipe(
        catchError(error => this.onError(win, errorMessage, errorButton, errorHeader)),
        filter(x => !!x),
        take(1)
      ).subscribe(key => {

          let reportUrl = `${environment.apiEndpoint}/Reports/${key}` + urlCallback;

          win.location.replace('/report-download.html?ts=' + ts + '&redir=' + encodeURIComponent(reportUrl));
          win['selfRef'] = win;
          win.focus();
        },
        _ => {
          let errorUrl = `${window.location.origin}/error`;
          win.location.href = errorUrl;
          win.focus();
        }
      );
  };

  onError(win: Window, message: string, button: string, header: string){

    win.close();

    this.sxDialogService.promptDialog(
      message, 
      button, 
      header
    );
    return new Observable<never>();
  }

  openLinkInNewWindow = (snackbarStringKey: string, linkUrl: Observable<string>) : Subscription =>{
    this.sxSnackbarService.openSnackBar(this.translate.instant(snackbarStringKey), '');

    let ts = '' + new Date().getTime();
    
    let win = window.open('/report-download.html?ts=' + ts);

    if (!win || win.closed || typeof win.closed=='undefined'){
      this.sxSnackbarService.openErrorSnackBar(this.translate.instant('products.popup-blocker'), this.translate.instant('products.popup-blocker-title'));
      return;
    }
    
    win['selfRef'] = win;

    return linkUrl
      .pipe(
        filter(x => !!x),
        take(1)
      ).subscribe(link => {
        if (!link) return;

        win.location.replace('/report-download.html?ts=' + ts + '&redir=' + encodeURIComponent(link));
        win['selfRef'] = win;
        win.focus();
      });
  };

  capitalize = s => {
    if (typeof s !== 'string') {
      return '';
    }
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

}
